/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { Txn } from '@src/app/model/txn';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { BlTxnService } from '@src/app/modules/txn/bl-txn.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { BankPageNavRoute } from '@src/app/routes/bank-page-nav';
import { BankInPageNavRoute } from '@src/app/routes/bank-in-page-nav';
import { BankOutPageNavRoute } from '@src/app/routes/bank-out-page-nav';

@Component({
    selector: 'app-bank-page',
    templateUrl: './bank-page.component.html',
    styleUrls: ['./bank-page.component.scss'],
    imports: [FormPageComponent]
})
export class BankPageComponent extends AbstractPageComponent {
    dataSvc = inject(BlTxnService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    protected BankAccountSvc = inject(BankAccountService);
    protected unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);


    readonly path = Txn.LEDGER.BL.code;

    config = new FormConfig({
        navRoute: BankPageNavRoute,
        title: Txn.LEDGER.BL.name,
        help: $localize`Bank transactions`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, {
                    cellOpts: { heading: 'Ref' }, readonly: true,
                    calculateValue: (o: Txn) => o.refNr
                }),
                FieldMaker.rev(),
                FormPicklistComponent.make('Type', 'typeId', 'typeName', { selfPopulate: true }, {
                    calculateValue: (o: Txn) => o.typeId
                }),
                FormDateComponent.make('Transaction Date', 'txnDate', { cellOpts: { width: '2%' }}),
                FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle', { service: this.cycleSvc, refreshes: ['periodId'] }),
                FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod', { service: this.periodSvc }, {
                    formColumn: 2, refresh: (o: Cycle, control: AppPicklistControl) => {
                        if (o) {
                            control.field.setPicklistItems(o.periods);
                            control.setValue(null);
                        }
                    },
                }),
                FormPicklistComponent.make('Bank Account', 'bankAccountId', null, {service: this.BankAccountSvc}, {formColumn: 3}),
                FormNavLinkComponent.makeDetail('Contact', 'ownerName', 'personId', PersonPageNavRoute),
                FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2', blankZero:true }, ),
                FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2', blankZero: true }),

                FormNumberComponent.make("Total", "total", { format: 'currency', width: 9, formatParms: '1.2-2' }, {
                    cellOpts: {style: 'white-space:nowrap; text-align:right'},
                    calculateValue: (o: Txn) => o.debit - o.credit, visible: Field.noShow
                }),
                FormButtonComponent.make('Matched', 'matched', {
                    type: 'icon', sendServer: false, label: 'Matched', visible: Field.noForm,
                    tableIconStyler: (o: Txn) => o.bankItemId ? {'color':'green'} : {'color':'red'},
                     
                    calculateValue: (o: Txn) => o.bankItemId ? 'check' : 'close'
                }),
                FieldMaker.idHolder('bankItemId'),

                new GridField({
                    field:
                        { label: 'Details', value: 'details', visible: Field.formOnly, formRow: 2 },
                    rowFactory: () => [
                        FieldMaker.id({ label: 'Ref', visible: { phone: true, computer: true, form: true }, footer: { text: 'Totals', style: 'text-align: right' } }),
                        FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
                            { service: this.bCodeSvc },
                            { cellOpts: { width: '50%' } }
                        ),
                        FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
                        FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
                        FieldMaker.notes(),
                        FieldMaker.rev(),
                        FieldMaker.deleteGridRow(),
                    ],
                    hasFooter: true
                }),
            ],
            formLayout: [
                { cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
                { cells: [{ colspan: 4, pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: () => of(new Txn()), // this.newFactory.bind(this),
        newOptions: [
            { name: $localize`New ${Txn.TYPE.BANK_IN.name}`, basePath: BankInPageNavRoute.newUrl, params: {}, id:'newReceipt' },
            { name: $localize`New ${Txn.TYPE.BANK_OUT.name}`, basePath: BankOutPageNavRoute.newUrl, params: {}, id:'newPayment' },
        ],
        pathEdit: (o: Txn) => Txn.getTxnLink(o)
    });

    newFactory(): Observable<Txn> {
        const txn = new Txn();
        txn.typeId = +this.activeRoute.snapshot.queryParamMap.get('typeId');
        console.warn(txn, this.activeRoute.snapshot);
        return of(txn);
    }
}
